import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo } from "react";
import { TABS_FA } from "../Model/Model";
import { SimpleSearch } from "../../../../reduxToolkit/Search/SearchApi";
import { TABLES } from "../../../../Apis/Tables";
import { GetNoAssigned } from "../../../../reduxToolkit/Devices/DevicesApi";
import DepartmentTotalEmployeesCard
    from "../../../../components/CustomAvailableAssignedItems/SubComponents/DepartmentTotalEmployeesCard";
import { filterByNameEmailPhoneNumber, getFullName } from "../../../../Apis/functions";
import SelectedDepartmentTotalEmployeesCard
    from "../../../../components/CustomAvailableAssignedItems/SubComponents/SelectedDepartmentTotalEmployeesCard";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 29 de 04 de 2026
 */

/**
 * Custom hook used to manage the configuration and rendering logic
 * for employee or department assignment views.
 *
 * This hook dynamically generates configuration objects depending on
 * the selected subview and handles synchronization of selected items.
 *
 * @function AddEmployeeApp
 *
 * @param {Object} props - Hook parameters.
 * @param {Array<Object>} props.selected - Selected employees or departments.
 * @param {string} props.subView - Current selected tab/subview.
 * @param {Function} props.setDeviceListCustomAccessOrDepartment - Setter used to synchronize selected items.
 * @param {number|string|null|undefined} props.id - Entity identifier used to determine create or edit mode.
 *
 * @returns {Object} Hook response object.
 * @returns {Function} returns.t - Translation function from react-i18next.
 * @returns {Object} returns.dynamicConfig - Dynamic configuration object depending on the current subview.
 */
export const AddEmployeeApp = ({
                                   selected,
                                   subView,
                                   setDeviceListCustomAccessOrDepartment,
                                   id
                               }) => {

    /**
     * Translation function from react-i18next.
     */
    const { t } = useTranslation();

    /**
     * Determines whether the component is in create mode.
     *
     * @type {boolean}
     */
    const isCreate = id === null || id === undefined;

    /**
     * Renders available department items.
     *
     * @param {Object} params - Render parameters.
     * @param {Object} params.item - Current item to render.
     * @param {string} params.keyId - Dynamic key identifier.
     * @param {boolean} params.disabled - Indicates whether the item is disabled.
     * @param {Function} params.onAdd - Callback executed when adding the item.
     *
     * @returns {JSX.Element} Rendered department card component.
     */
    const renderAvailableItem = ({ item, keyId, disabled, onAdd }) => (
        <DepartmentTotalEmployeesCard
            item={{
                [keyId]: item[keyId],
                fullName: item?.fullName || getFullName(
                    item?.name,
                    item?.firstLastName,
                    item?.secondLastName
                ),
                totalEmployees: item?.totalEmployees,
                disabled
            }}
            onAdd={onAdd}
        />
    );

    /**
     * Renders selected department items.
     *
     * Applies search filtering before rendering selected items.
     *
     * @param {Object} params - Render parameters.
     * @param {Array<Object>} params.items - List of selected items.
     * @param {string} params.keyId - Dynamic key identifier.
     * @param {string} params.selectedSearchElement - Search term used for filtering.
     * @param {Function} params.handleDelete - Callback executed when deleting an item.
     *
     * @returns {Array<JSX.Element>} Rendered selected department components.
     */
    const renderSelectedItems = ({
                                     items,
                                     keyId,
                                     selectedSearchElement,
                                     handleDelete
                                 }) => (
        filterByNameEmailPhoneNumber(items, selectedSearchElement)?.map((item, index) => (
            <div
                className={"mb-3"}
                key={item[keyId] || index}
            >
                <SelectedDepartmentTotalEmployeesCard
                    keyId={keyId}
                    item={{ ...item, disabled: false }}
                    onDelete={handleDelete}
                />
            </div>
        ))
    );

    /**
     * Generates a dynamic configuration object depending on the selected subview.
     *
     * @type {Object}
     */
    const dynamicConfig = useMemo(() => {

        /**
         * Configuration used for custom access employee assignment.
         */
        if (subView === TABS_FA.CUSTOM_ACCESS.key) {
            return {
                labelRowsPerPage: 'employees_per_page',
                tableNotInformationLegend: 'you_have_not_assigned_any_employee_yet',
                keyId: 'userId',
                titleContainer: 'available_employees',
                selectedSectionTitle: 'selected_employees',
                searchTitle: 'search_by_name_email_or_phone',
                api: isCreate ? SimpleSearch : GetNoAssigned,
                model: isCreate
                    ? TABLES.USER_BY_USER_TYPE
                    : TABLES.DEVICE_CUSTOM_ACCESS,
                userTypeIds: [1]
            };
        }

        /**
         * Configuration used for department assignment.
         */
        if (subView === TABS_FA.DEPARTMENTS.key) {
            return {
                labelRowsPerPage: 'departments_per_page',
                tableNotInformationLegend: 'you_have_not_assigned_any_department_yet',
                keyId: 'id',
                titleContainer: "available_departments",
                selectedSectionTitle: "selected_departments",
                searchTitle: 'search_by_name',
                api: isCreate ? SimpleSearch : GetNoAssigned,
                model: isCreate
                    ? TABLES.DEPARTMENT_TOTAL_EMPLOYEES
                    : TABLES.DEVICE_DEPARTMENT,
                renderAvailableItem: renderAvailableItem,
                renderSelectedItems: renderSelectedItems
            };
        }

        /**
         * Default empty configuration.
         */
        return {};

    }, [subView]);

    /**
     * Synchronizes selected items with external state.
     */
    useEffect(() => {
        setDeviceListCustomAccessOrDepartment(selected || []);
    }, [selected]);

    return {
        t,
        dynamicConfig
    };
};
